/**
 * Centralized API client
 * Handles all HTTP requests with authentication headers
 */

import { API_BASE_URL } from '../config';
import { clearAuthStorage } from '../utils/authStorage';
import { silentTokenRefresh } from '../hooks/useInactivityLogout';
import type {
  EnabledModules,
  AiUseCase,
  AiUseCaseStatus,
  MeetingMinutes,
  MinutesStatus,
  ProcessingActivity,
  ActivityStatus,
  AiUseCaseAuditEvent,
} from '../types/models';

// Auth headers matching existing jQuery pattern + JWT Bearer token
export const getAuthHeaders = (): Record<string, string> => {
  const tenantId = localStorage.getItem('tenantId') || '';
  const userId = localStorage.getItem('userId') || '';
  const idToken = localStorage.getItem('idToken') || '';
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-Id': tenantId,
    'X-User-Id': userId,
  };
  
  // Add JWT Bearer token for Lambda authorizer validation
  if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`;
  }
  
  return headers;
};

// Generic fetch wrapper with auth
async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;

  const makeRequest = () =>
    fetch(url, {
      ...options,
      headers: {
        ...getAuthHeaders(),
        ...options.headers,
      },
    });

  let response = await makeRequest();

  // ── 401: token likely expired — attempt silent refresh, then retry once ──
  if (response.status === 401) {
    console.warn('Got 401 — attempting silent token refresh before logging out.');
    await silentTokenRefresh();
    response = await makeRequest(); // retry with potentially-new token

    if (response.status === 401) {
      // Refresh didn't help — the session is genuinely dead
      console.error('Session expired and refresh failed — redirecting to login.');
      const redirectPath = window.location.pathname + window.location.search || '/dashboard';
      clearAuthStorage();
      try { sessionStorage.setItem('redirectAfterLogin', redirectPath); } catch { /* ignore */ }
      window.location.href = '/login';
      throw new Error('Session expired');
    }
  }

  // ── 403: permission denied — the token is valid, the user just doesn't have
  //    access to this resource.  Do NOT log them out; surface as a normal error. ──
  if (response.status === 403) {
    throw new Error(`Access denied (403): ${endpoint}`);
  }

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`API Error: ${response.status} - ${error}`);
  }

  // Many DELETE endpoints return 204 No Content; also allow empty bodies.
  if (response.status === 204) {
    return undefined as unknown as T;
  }

  const contentType = response.headers.get('content-type') || '';
  const text = await response.text();
  if (!text) {
    return undefined as unknown as T;
  }

  if (contentType.includes('application/json')) {
    return JSON.parse(text) as T;
  }

  // Fallback: attempt JSON parse; otherwise return raw text.
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

/** Unwraps the `API Error: <status> - <json-or-text-body>` shape fetchWithAuth throws above into
 * a status code plus the backend's own human-written {error: "..."} message - so a mutation's
 * onError can show a caller-written 400 message ("A document cannot be its own subordinate")
 * instead of the raw, brace-and-quote-included response text. Falls back to the raw message
 * when the body isn't that shape at all (a network failure, a non-API thrown Error, etc). */
export function parseApiError(err: unknown): { status: number | null; message: string } {
  const raw = err instanceof Error ? err.message : String(err);
  const match = raw.match(/^API Error: (\d+) - ([\s\S]*)$/);
  if (!match) return { status: null, message: raw };
  const status = Number(match[1]);
  try {
    const parsed = JSON.parse(match[2]) as { error?: string };
    return { status, message: parsed.error || raw };
  } catch {
    return { status, message: raw };
  }
}

// API methods
export const api = {
  // GET request
  get: <T>(endpoint: string, options?: RequestInit): Promise<T> => {
    return fetchWithAuth<T>(endpoint, { method: 'GET', ...options });
  },

  // POST request
  post: <T>(endpoint: string, data: unknown): Promise<T> => {
    return fetchWithAuth<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // PUT request
  put: <T>(endpoint: string, data: unknown): Promise<T> => {
    return fetchWithAuth<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // DELETE request
  delete: <T>(endpoint: string): Promise<T> => {
    return fetchWithAuth<T>(endpoint, { method: 'DELETE' });
  },
};

// Specific API endpoints for type safety
export const riskApi = {
  getAll: async (registerId: string) => {
    const response = await api.get<{ risks: any[] }>(`/risks?registerId=${registerId}`);
    return response.risks || [];
  },

  // Used by admin pages; backend supports listing without registerId.
  listAll: async () => {
    const response = await api.get<{ risks: any[] }>(`/risks`);
    return response.risks || [];
  },
  
  getOne: (riskId: string) => 
    api.get<any>(`/risks/${riskId}`),
  
  create: (data: any) => 
    api.post<any>('/risks', data),
  
  update: (riskId: string, data: any) => 
    api.put<any>(`/risks/${riskId}`, data),
  
  delete: (riskId: string) => 
    api.delete(`/risks/${riskId}`),
};

export const configApi = {
  getFieldConfigs: async (registerId: string) => {
    const response = await api.get<{ configs: any[] }>(`/config/register/${registerId}`);
    return response.configs || [];
  },

  getCardDefinitions: async (registerId: string) => {
    const response = await api.get<{ cards: Record<string, string> }>(
      `/config/register/${registerId}/cards`
    );
    return response.cards || {};
  },
  
  getRegisterConfig: (registerId: string) => 
    api.get<any>(`/registers/${registerId}`),

  applyRegisterTemplate: (registerId: string, template: string) =>
    api.post<any>(`/config/register/${registerId}/template`, { template }),

  applyMatrixTemplate: (matrixId: string, template: string) =>
    api.post<any>(`/config/matrix/${matrixId}/template`, { template }),
};

export type RegisterConfigKind =
  | 'risk'
  | 'incident'
  | 'issue'
  | 'remediation'
  | 'threat'
  | 'evidence';

const getRegisterConfigBasePath = (kind: RegisterConfigKind): string => {
  if (kind === 'risk') return '/config/register';
  return `/config/${kind}-register`;
};

export const registerConfigApi = {
  getFieldConfigs: async (kind: RegisterConfigKind, registerId: string) => {
    const base = getRegisterConfigBasePath(kind);
    const response = await api.get<{ configs: any[] }>(`${base}/${registerId}`);
    return response.configs || [];
  },

  getCardDefinitions: async (kind: RegisterConfigKind, registerId: string) => {
    const base = getRegisterConfigBasePath(kind);
    const response = await api.get<{ cards: Record<string, string> }>(
      `${base}/${registerId}/cards`
    );
    return response.cards || {};
  },

  getModuleConfig: async (kind: RegisterConfigKind, registerId: string) => {
    const base = getRegisterConfigBasePath(kind);
    const response = await api.get<{ enabledModules: EnabledModules }>(
      `${base}/${registerId}/modules`
    );
    return response.enabledModules || {
      documentManager: true,
      raciManager: true,
      linkedArtefacts: true,
      requirementsTracker: true,
    };
  },

  createFieldConfig: async (kind: RegisterConfigKind, registerId: string, payload: any) => {
    const base = getRegisterConfigBasePath(kind);
    return api.post<any>(`${base}/${registerId}`, { registerId, ...payload });
  },

  updateFieldConfig: async (configId: string, payload: any) => {
    return api.put<any>(`/config/${encodeURIComponent(configId)}`, payload);
  },

  deleteFieldConfig: async (configId: string) => {
    return api.delete<any>(`/config/${encodeURIComponent(configId)}`);
  },

  applyTemplate: async (kind: RegisterConfigKind, registerId: string, template: string) => {
    const base = getRegisterConfigBasePath(kind);
    return api.post<any>(`${base}/${registerId}/template`, { template });
  },

  updateCards: async (kind: RegisterConfigKind, registerId: string, cards: Record<string, string>) => {
    const base = getRegisterConfigBasePath(kind);
    return api.put<any>(`${base}/${registerId}/cards`, { cards });
  },
};

type EntityApi = {
  getOne: (id: string) => Promise<any>;
  create: (data: any) => Promise<any>;
  update: (id: string, data: any) => Promise<any>;
  delete: (id: string) => Promise<any>;
};

const makeEntityApi = (basePath: string): EntityApi => ({
  getOne: (id: string) => api.get<any>(`${basePath}/${id}`),
  create: (data: any) => api.post<any>(basePath, data),
  update: (id: string, data: any) => api.put<any>(`${basePath}/${id}`, data),
  delete: (id: string) => api.delete<any>(`${basePath}/${id}`),
});

export const incidentApi = makeEntityApi('/incidents');
export const issueApi = makeEntityApi('/issues');
export const remediationApi = makeEntityApi('/remediations');
export const threatApi = makeEntityApi('/threats');
// Evidence uses singular base path in the legacy UI
export const evidenceApi = makeEntityApi('/evidence');

// excludedTeams: which teams this requirement does NOT apply to, by the same free-typed teamName
// string used everywhere else in this system (there is no stable team id to key on - see
// pages/EvidenceRecordDetail.tsx). Absent or empty = applies to every team, present and future -
// the backward-compatible default every pre-existing evidence record already satisfies.
// Deliberately an EXCLUSION list, not an inclusion list: most requirements apply to most teams
// with only occasional per-team carve-outs (that's the whole reason a matrix beats a per-team
// list here), and an exclusion list represents that directly - excluding one team never touches
// any other team's coverage, including a team added to the roster later. An inclusion list would
// need to materialise into an explicit array the first time ANY team is excluded, which silently
// stops covering every team added afterward, and collides with its own "empty = all" default the
// moment the last covered team is excluded (a real bug this codebase specifically had and fixed).
export type Deliverable = { id: string; description: string; status?: string; dueDate?: string; excludedTeams?: string[] };

export type EvidenceFormat = 'config_export' | 'screenshots' | 'ticket_links';

/** A link attachment isn't a file - it never goes through documentsApi, it's carried
 * directly on the submission record (lambda/prod-nimbus-evidence create_submission). */
export type LinkAttachment = { id: string; url: string; description: string; capturedAt: string };

export const evidenceSubmissionsApi = {
  list: (evidenceId: string) => api.get<any>(`/evidence/${evidenceId}/submissions`),
  create: (evidenceId: string, data: {
    notes?: string; files?: string[]; teamName?: string; teamDepartmentId?: string; deliverables?: Deliverable[];
    owner?: string; scopeDescription?: string; evidenceFormat?: EvidenceFormat; linkAttachments?: LinkAttachment[];
  }) =>
    api.post<any>(`/evidence/${evidenceId}/submissions`, data),
  review: (
    evidenceId: string,
    submissionId: string,
    data: { action: 'Accepted' | 'Rejected' | 'Clarification Needed'; reviewNote?: string; strengthOverride?: string }
  ) => api.put<any>(`/evidence/${evidenceId}/submissions/${submissionId}`, data),
  // Content-only edit (teamName/deliverables) - same PUT route as review, but with no `action`
  // in the body, review_submission's own content-edit branch handles it without touching status.
  // Rejected with a 409 by the backend once the submission is locked - reopen() first.
  update: (evidenceId: string, submissionId: string, data: { teamName?: string; deliverables?: Deliverable[] }) =>
    api.put<any>(`/evidence/${evidenceId}/submissions/${submissionId}`, data),
  /** Unlocks a submitted round for editing. Backend-gated to the team's own owner/delegate
   * or an admin (lambda/prod-nimbus-evidence _can_act_for_team) - this call can still come
   * back as a 403, same as any other write here. */
  reopen: (evidenceId: string, submissionId: string) =>
    api.put<any>(`/evidence/${evidenceId}/submissions/${submissionId}`, { action: 'reopen' }),
  delete: (evidenceId: string, submissionId: string) =>
    api.delete<any>(`/evidence/${evidenceId}/submissions/${submissionId}`),
  comment: (evidenceId: string, submissionId: string, text: string) =>
    api.post<any>(`/evidence/${evidenceId}/submissions/${submissionId}/comments`, { text }),
  listAllPending: () => api.get<any>(`/evidence/submissions`),
};

export const controlApi = makeEntityApi('/controls');

// List helpers (some legacy pages rely on listing without query params)
export const listApi = {
  incidents: async () => {
    const response = await api.get<{ incidents: any[] }>(`/incidents`);
    return response.incidents || [];
  },
  issues: async () => {
    const response = await api.get<{ issues: any[] }>(`/issues`);
    return response.issues || [];
  },
  remediations: async () => {
    const response = await api.get<{ remediations: any[] }>(`/remediations`);
    return response.remediations || [];
  },
  threats: async () => {
    const response = await api.get<{ threats: any[] }>(`/threats`);
    return response.threats || [];
  },
  evidence: async () => {
    const response = await api.get<{ evidence: any[] }>(`/evidence`);
    return response.evidence || [];
  },
  controls: async () => {
    const response = await api.get<{ controls: any[] }>(`/controls`);
    return response.controls || [];
  },
  documents: async () => {
    const response = await api.get<{ documents: any[] }>(`/documents`);
    return response.documents || [];
  },
};

export const controlMatrixApi = {
  getOne: (matrixId: string) => api.get<any>(`/control-matrices/${matrixId}`),
  getAll: async () => {
    const response = await api.get<{ matrices: any[] }>(`/control-matrices`);
    return response.matrices || [];
  },
  create: (data: any) => api.post<any>(`/control-matrices`, data),
  update: (matrixId: string, data: any) => api.put<any>(`/control-matrices/${matrixId}`, data),
  delete: (matrixId: string) => api.delete<any>(`/control-matrices/${matrixId}`),
};

export const templateApi = {
  /**
   * Get all templates, optionally filtered by category
   */
  list: async (category?: string) => {
    const endpoint = category 
      ? `/config/templates?category=${encodeURIComponent(category)}`
      : '/config/templates';
    const response = await api.get<{ templates: any[] }>(endpoint);
    return response.templates || [];
  },
  
  /**
   * Get a specific template by ID
   */
  getById: async (templateId: string) => {
    return api.get<any>(`/config/templates/${templateId}`);
  },
  
  /**
   * Create a new template (admin only)
   */
  create: async (data: {
    templateName: string;
    category: string;
    displayName: string;
    description?: string;
    isActive?: boolean;
    fields: Array<{
      fieldId: string;
      cardNumber: number;
      displayOrder: number;
      overrides?: Record<string, any>;
    }>;
    cards: Record<string, string>;
    enabledModules?: EnabledModules;
  }) => {
    return api.post<any>('/config/templates', data);
  },
  
  /**
   * Update an existing template (admin only)
   */
  update: async (templateId: string, data: Partial<{
    displayName: string;
    description: string;
    isActive: boolean;
    fields: Array<{
      fieldId: string;
      cardNumber: number;
      displayOrder: number;
      overrides?: Record<string, any>;
    }>;
    cards: Record<string, string>;
    enabledModules: EnabledModules;
  }>) => {
    return api.put<any>(`/config/templates/${templateId}`, data);
  },
  
  /**
   * Delete a template (admin only)
   */
  delete: async (templateId: string) => {
    return api.delete<any>(`/config/templates/${templateId}`);
  },
};

export const catalogApi = {
  listByCategory: async (category: string) => {
    const response = await api.get<{ fields: any[] }>(
      `/catalog?category=${encodeURIComponent(category)}`
    );
    return response.fields || [];
  },
  getAll: async () => {
    const response = await api.get<{fields: any[]}>('/catalog');
    return response.fields || [];
  },
  create: async (data: any) => {
    return api.post<any>('/catalog', data);
  },
  update: async (catalogId: string, data: any) => {
    return api.put<any>(`/catalog/${catalogId}`, data);
  },
  delete: async (catalogId: string) => {
    return api.delete<any>(`/catalog/${catalogId}`);
  },
};

export const documentApi = {
  getRegisters: async () => {
    const response = await api.get<{ registers: any[] }>(`/registers?type=document`);
    return response.registers || [];
  },
  listByRegister: async (registerId: string) => {
    const response = await api.get<{ documents: any[] }>(
      `/documents?entityType=register&entityId=${encodeURIComponent(registerId)}`
    );
    return response.documents || [];
  },
  /** Every document for the tenant, register-parented or not - the backend's own
   *  list_documents already supports this (a plain GET /documents with no entityType/entityId
   *  falls through to a tenant-wide scan; confirmed already used by MaintenanceTasks). Needed
   *  because a document like a DPIA is entity-linked (to the AI Use Case or Processing Activity
   *  that created it) rather than register-parented by design, so it never appeared under any
   *  register and was otherwise unreachable from DocumentsList at all short of its raw URL. */
  listAll: async () => {
    const response = await api.get<{ documents: any[] }>('/documents');
    return response.documents || [];
  },
  getOne: (documentId: string) => api.get<any>(`/documents/${documentId}`),
  create: (data: any) => api.post<any>('/documents', data),
  update: (documentId: string, data: any) => api.put<any>(`/documents/${documentId}`, data),
  delete: (documentId: string, force = true) =>
    api.delete<any>(`/documents/${documentId}${force ? '?force=true' : ''}`),
  publish: (documentId: string, versionType: 'major' | 'minor' = 'minor', changeReason?: string) =>
    api.post<any>(`/documents/${documentId}/publish`, { versionType, changeReason }),
  getVersions: (documentId: string) => api.get<any>(`/documents/${documentId}/versions`),
  createVersion: (documentId: string, data: any) => api.post<any>(`/documents/${documentId}/versions`, data),
  /** editedHtml is optional - omit to accept the AI-proposed text as-is. */
  acceptPendingChange: (documentId: string, changeId: string, editedHtml?: string) =>
    api.post<any>(`/documents/${documentId}/pending-changes/${changeId}/accept`, editedHtml ? { editedHtml } : {}),
  rejectPendingChange: (documentId: string, changeId: string) =>
    api.post<any>(`/documents/${documentId}/pending-changes/${changeId}/reject`, {}),
  /** Part 4's per-dimension risk score - only meaningful on a documentType: 'DPIA' document. */
  upsertRiskDimension: (
    documentId: string,
    dimension: string,
    data: { riskScore: 'low' | 'medium' | 'high' | 'maximum'; mitigation: string; residualRiskAcceptable: boolean },
  ) => api.put<any>(`/documents/${documentId}/risk-dimensions/${encodeURIComponent(dimension)}`, data),
};

export const systemDescriptionApi = {
  generate: (data: { periodStart: string; periodEnd: string; contextTier: 'low' | 'high'; sourceDocumentIds?: string[] }) =>
    api.post<{ reportId: string; status: string }>('/system-description/generate', data),
  listReports: async () => {
    const response = await api.get<{ reports: any[] }>('/system-description/reports');
    return response.reports || [];
  },
  getReport: (reportId: string) => api.get<any>(`/system-description/reports/${reportId}`),
  deleteReport: (reportId: string) => api.delete<{ deleted: boolean }>(`/system-description/reports/${reportId}`),
  gapAnalysis: (currentReportId: string, previousReportId: string) =>
    api.post<{ changedSections: string[]; summary: string }>('/system-description/gap-analysis', { currentReportId, previousReportId }),
};

export const peopleApi = {
  getAll: async () => {
    const response = await api.get<{ people: any[] }>('/people');
    const people = response.people || [];
    
    // Construct personName from firstName, lastName, and department
    return people.map((person) => {
      const name = [person.firstName, person.lastName].filter(Boolean).join(' ').trim() ||
        person.email || 
        person.personId;
      
      const department = person.departmentName || person.department || '';
      
      return {
        ...person,
        personName: department ? `${name} (${department})` : name
      };
    });
  },
  
  getOne: (personId: string) => 
    api.get<any>(`/people/${personId}`),

  create: (data: any) => api.post<any>('/people', data),
  update: (personId: string, data: any) => api.put<any>(`/people/${personId}`, data),
  delete: (personId: string) => api.delete<any>(`/people/${personId}`),
};

export const assetsApi = {
  getAll: async () => {
    const response = await api.get<{ assets: any[] }>('/assets');
    return response.assets || [];
  },

  getOne: (assetId: string) => api.get<any>(`/assets/${assetId}`),
  create: (data: any) => api.post<any>('/assets', data),
  update: (assetId: string, data: any) => api.put<any>(`/assets/${assetId}`, data),
  delete: (assetId: string) => api.delete<any>(`/assets/${assetId}`),
};

export const raciApi = {
  getAll: async () => {
    const response = await api.get<{ assignments: any[] }>(`/raci`);
    return response.assignments || [];
  },

  getByArtefact: async (artefactType: string, artefactId: string) => {
    const response = await api.get<any>(`/raci?${artefactType}Id=${encodeURIComponent(artefactId)}`);
    return response.assignments || [];
  },
  
  create: (data: any) => 
    api.post<any>('/raci', data),
  
  update: (raciId: string, data: any) => 
    api.put<any>(`/raci/${raciId}`, data),
  
  delete: (raciId: string) => 
    api.delete(`/raci/${raciId}`),

  batchCreate: (assignments: Array<{ artefactType: string; artefactId: string; personId: string; role: string; notes?: string }>) =>
    api.post<{ created: number; skipped: number }>('/raci/batch', { assignments }),

  replacePerson: (fromPersonId: string, toPersonId: string, role?: string) =>
    api.put<{ updated: number; failed: number }>('/raci/replace', { fromPersonId, toPersonId, ...(role ? { role } : {}) }),
};

export const linkedArtefactsApi = {
  getLinks: (artefactId: string, artefactType: string) => 
    api.get<any>(`/artefacts/${encodeURIComponent(artefactId)}/links?type=${encodeURIComponent(artefactType)}`),
  
  search: (query: string, typeFilter?: string, excludeLinkedTo?: string, signal?: AbortSignal) => {
    const params = new URLSearchParams({ q: query });
    if (typeFilter) params.append('type_filter', typeFilter);
    if (excludeLinkedTo) params.append('exclude_linked_to', excludeLinkedTo);
    return api.get<any>(`/artefacts/search?${params.toString()}`, { signal });
  },
  
  createLinks: (data: any) => 
    api.post<any>('/artefacts/links', data),
  
  deleteLink: (sourceId: string, sourceType: string, targetId: string, targetType: string) =>
    fetchWithAuth<any>('/artefacts/link', {
      method: 'DELETE',
      body: JSON.stringify({ source_id: sourceId, source_type: sourceType, target_id: targetId, target_type: targetType }),
    }),
};

export const securityObjectivesApi = {
  getAll: async () => {
    const response = await api.get<{ securityObjectives: any[] }>('/security-objectives');
    return response.securityObjectives || [];
  },
  getOne: (objectiveId: string) => api.get<any>(`/security-objectives/${objectiveId}`),
  create: (data: any) => api.post<any>('/security-objectives', data),
  update: (objectiveId: string, data: any) => api.put<any>(`/security-objectives/${objectiveId}`, data),
  delete: (objectiveId: string) => api.delete<any>(`/security-objectives/${objectiveId}`),
};

export const departmentsApi = {
  getAll: async () => {
    const response = await api.get<{ departments: any[] }>('/departments');
    return response.departments || [];
  },
  getOne: (departmentId: string) => api.get<any>(`/departments/${departmentId}`),
  create: (data: any) => api.post<any>('/departments', data),
  update: (departmentId: string, data: any) => api.put<any>(`/departments/${departmentId}`, data),
  delete: (departmentId: string) => api.delete<any>(`/departments/${departmentId}`),
};

export const physicalSitesApi = {
  getAll: async () => {
    const response = await api.get<{ physicalSites: any[] }>('/physical-sites');
    return response.physicalSites || [];
  },
  getOne: (siteId: string) => api.get<any>(`/physical-sites/${siteId}`),
  create: (data: any) => api.post<any>('/physical-sites', data),
  update: (siteId: string, data: any) => api.put<any>(`/physical-sites/${siteId}`, data),
  delete: (siteId: string) => api.delete<any>(`/physical-sites/${siteId}`),
};

export const interestedPartiesApi = {
  getAll: async () => {
    const response = await api.get<{ interestedParties: any[] }>('/interested-parties');
    return response.interestedParties || [];
  },
  getOne: (partyId: string) => api.get<any>(`/interested-parties/${partyId}`),
  create: (data: any) => api.post<any>('/interested-parties', data),
  update: (partyId: string, data: any) => api.put<any>(`/interested-parties/${partyId}`, data),
  delete: (partyId: string) => api.delete<any>(`/interested-parties/${partyId}`),
};

export const legalRegisterApi = {
  getAll: async () => {
    const response = await api.get<{ legalRequirements: any[] }>('/legal-register');
    return response.legalRequirements || [];
  },
  getOne: (lawId: string) => api.get<any>(`/legal-register/${lawId}`),
  create: (data: any) => api.post<any>('/legal-register', data),
  update: (lawId: string, data: any) => api.put<any>(`/legal-register/${lawId}`, data),
  delete: (lawId: string) => api.delete<any>(`/legal-register/${lawId}`),
};

export const subprocessorsApi = {
  getAll: async () => {
    const response = await api.get<{ subprocessors: any[] }>('/subprocessors');
    return response.subprocessors || [];
  },
  getOne: (subprocessorId: string) => api.get<any>(`/subprocessors/${subprocessorId}`),
  create: (data: any) => api.post<any>('/subprocessors', data),
  update: (subprocessorId: string, data: any) => api.put<any>(`/subprocessors/${subprocessorId}`, data),
  delete: (subprocessorId: string) => api.delete<any>(`/subprocessors/${subprocessorId}`),
};

export const registersApi = {
  getAll: async () => {
    const response = await api.get<{ registers: any[] }>('/registers?type=risk');
    return response.registers || [];
  },
  getOne: (registerId: string) => api.get<any>(`/registers/${registerId}`),
  create: (data: any) => api.post<any>('/registers', data),
  update: (registerId: string, data: any) => api.put<any>(`/registers/${registerId}`, data),
  delete: (registerId: string, force?: boolean) => api.delete<any>(force ? `/registers/${registerId}?force=true` : `/registers/${registerId}`),
};

type RegisterAdminApi = {
  getAll: () => Promise<any[]>;
  getOne: (registerId: string) => Promise<any>;
  create: (data: any) => Promise<any>;
  update: (registerId: string, data: any) => Promise<any>;
  delete: (registerId: string, force?: boolean) => Promise<any>;
};

const makeRegisterAdminApi = (basePath: string): RegisterAdminApi => ({
  getAll: async () => {
    const response = await api.get<{ registers: any[] }>(basePath);
    return response.registers || [];
  },
  getOne: (registerId: string) => api.get<any>(`${basePath}/${registerId}`),
  create: (data: any) => api.post<any>(basePath, data),
  update: (registerId: string, data: any) => api.put<any>(`${basePath}/${registerId}`, data),
  delete: (registerId: string, force?: boolean) => api.delete<any>(force ? `${basePath}/${registerId}?force=true` : `${basePath}/${registerId}`),
});

// Factory function to create register API based on type
export const createRegisterApi = (type: string): RegisterAdminApi => {
  // Special case: document registers use /registers with type param
  if (type === 'document') {
    return {
      getAll: async () => {
        const response = await api.get<{ registers: any[] }>(`/registers?type=document`);
        return response.registers || [];
      },
      getOne: (registerId: string) => api.get<any>(`/registers/${registerId}`),
      create: (data: any) => api.post<any>('/registers', { ...data, type: 'document' }),
      update: (registerId: string, data: any) => api.put<any>(`/registers/${registerId}`, { ...data, type: 'document' }),
      delete: (registerId: string) => api.delete<any>(`/registers/${registerId}`),
    };
  }
  
  // Special case: risk registers use /registers (legacy)
  if (type === 'risk') {
    return makeRegisterAdminApi('/registers');
  }
  
  // Standard case: /{type}-registers
  return makeRegisterAdminApi(`/${type}-registers`);
};

// Backward compatibility - keep existing exports for now
export const incidentRegistersApi = makeRegisterAdminApi('/incident-registers');
export const issueRegistersApi = makeRegisterAdminApi('/issue-registers');
export const remediationRegistersApi = makeRegisterAdminApi('/remediation-registers');
export const threatRegistersApi = makeRegisterAdminApi('/threat-registers');
export const evidenceRegistersApi = makeRegisterAdminApi('/evidence-registers');
export const documentRegistersApi = createRegisterApi('document');

export const usersApi = {
  getAll: () => api.get<any>(`/users`),
  invite: (data: any) => api.post<any>(`/users/invite`, data),
  update: (userId: string, data: any) => api.put<any>(`/users/${userId}`, data),
  delete: (userId: string) => api.delete<any>(`/users/${userId}`),
  me: () => api.get<any>(`/users/me`),
  resetPassword: (userId: string) => api.post<any>(`/users/${userId}/reset-password`, {}),
};

export const invitationsApi = {
  delete: (invitationId: string) => api.delete<any>(`/invitations-pending/${invitationId}`),
};

export const adminFrameworksApi = {
  getAll: () => api.get<any[]>(`/admin/frameworks`),
  getOne: (frameworkId: string) => api.get<any>(`/admin/frameworks/${frameworkId}`),
  create: (data: any) => api.post<any>(`/admin/frameworks`, data),
  delete: (frameworkId: string) => api.delete<any>(`/admin/frameworks/${frameworkId}`),
};

export const dataManagementApi = {
  exportData: () => api.get<{ downloadUrl: string; exportDate: string }>(`/data-management/export`),
  deleteAccount: (confirmation: string) =>
    api.post<{ message: string; scheduledDeletionDate: string; usersAffected: number }>(
      `/data-management/delete-account`,
      { confirmation }
    ),
};

export const adminMappingsApi = {
  list: (sourceFrameworkId: string, targetFrameworkId: string) =>
    api.get<any[]>(
      `/admin/mappings?sourceFrameworkId=${encodeURIComponent(sourceFrameworkId)}&targetFrameworkId=${encodeURIComponent(targetFrameworkId)}`
    ),
  bulkCreate: (mappings: any[]) => api.post<any>(`/admin/mappings/bulk`, { mappings }),
};

/** Fetch pre-mapped evidence titles stored in DynamoDB (replaces hardcoded frontend maps). */
export const evidenceTemplatesApi = {
  /**
   * Given a list of framework codes (e.g. ['ISO27001', 'SOC2']), fetch all
   * control → evidence-title mappings from the API and merge them into a single
   * Record<controlId, string[]> suitable for the evidence generation mutation.
   */
  getByFrameworks: async (frameworkCodes: string[]): Promise<Record<string, string[]>> => {
    const results = await Promise.all(
      frameworkCodes.map((code) =>
        api.get<{ templates: Array<{ controlId: string; titles: string[] }> }>(
          `/evidence-templates?framework=${encodeURIComponent(code)}`
        )
      )
    );
    const merged: Record<string, string[]> = {};
    for (const result of results) {
      for (const { controlId, titles } of result.templates || []) {
        merged[controlId] = titles;
      }
    }
    return merged;
  },
};

export const documentsApi = {
  listAll: async () => {
    const response = await api.get<{ documents: any[] }>('/documents');
    return response.documents || [];
  },
  getAll: async (entityType: string, entityId: string) => {
    const response = await api.get<{ documents: any[] }>(`/documents?entityType=${entityType}&entityId=${entityId}`);
    return response.documents || [];
  },
  upload: async (
    entityType: string, entityId: string, file: File, onProgress?: (progress: number) => void,
    /** When the evidence itself was captured, not when it was uploaded - e.g. a screenshot
     * taken weeks ago and only attached today. Defaults to the file's own last-modified
     * date, which is the best available proxy absent an explicit override. */
    capturedAt?: string,
  ): Promise<{ documentId: string; fileName: string }> => {
    // Step 1: Request presigned URL
    const uploadRequest = await api.post<any>('/documents/upload-url', {
      entityType,
      entityId,
      filename: file.name,
      fileType: file.type,
      fileSize: file.size,
      capturedAt: capturedAt || new Date(file.lastModified).toISOString(),
    });

    // Step 2: Upload to S3 using presigned POST
    const formData = new FormData();
    
    // Add all fields from the presigned POST policy
    Object.keys(uploadRequest.fields).forEach(key => {
      formData.append(key, uploadRequest.fields[key]);
    });
    
    // File must be added last
    formData.append('file', file);
    
    return new Promise<{ documentId: string; fileName: string }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 204 || xhr.status === 200) {
          // Step 3: Confirm upload. Resolve with the new document's own id/fileName - callers
          // that need to reference the uploaded file afterwards (e.g. embedding it inline in
          // rich text) otherwise have no way to get it back out of this promise.
          api.post<any>(`/documents/${uploadRequest.documentId}/confirm`, {
            entityType,
            entityId,
          }).then(() => resolve({ documentId: uploadRequest.documentId, fileName: file.name })).catch(reject);
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Upload failed')));
      xhr.open('POST', uploadRequest.url);
      xhr.send(formData);
    });
  },
  download: (documentId: string, fileName: string) => {
    // Request download URL and trigger download
    api.get<{ downloadUrl: string }>(`/documents/${documentId}/download`).then(response => {
      const link = document.createElement('a');
      link.href = response.downloadUrl;
      link.download = fileName;
      link.click();
    });
  },
  delete: (documentId: string) => api.delete<any>(`/documents/${documentId}`),
  /**
   * Batch-resolve inline document images (uploaded with entityType 'document-image') to
   * short-lived, inline-disposition URLs. Content never stores a raw URL - see
   * PolicyEditorImage.ts - only a stable id, because the bucket is private and a persisted
   * URL would eventually go dead. One call per document open, not one per image.
   */
  resolveImageUrls: (imageIds: string[]): Promise<Record<string, string | null>> =>
    imageIds.length === 0
      ? Promise.resolve({})
      : api.post<{ urls: Record<string, string | null> }>('/documents/resolve-images', { imageIds }).then((r) => r.urls),
};

// Readiness Assessment API
export const readinessApi = {
  list: () => api.get<{ assessments: any[] }>('/readiness'),
  create: (data: { title?: string; description?: string }) =>
    api.post<{ assessment: any }>('/readiness', data),
  get: (assessmentId: string) =>
    api.get<{ assessment: any }>(`/readiness/${assessmentId}`),
  update: (assessmentId: string, data: { responses?: Record<string, any>; title?: string; description?: string }) =>
    api.put<{ assessment: any }>(`/readiness/${assessmentId}`, data),
  submit: (assessmentId: string, data: { responses?: Record<string, any> }) =>
    api.post<{ assessment: any }>(`/readiness/${assessmentId}/submit`, data),
  delete: (assessmentId: string) =>
    api.delete<any>(`/readiness/${assessmentId}`),
  getQuestions: () =>
    api.get<{ questions: any[] }>('/readiness/questions'),
};

// ISMS Scope Document API (single document per tenant)
export const ismsScopeApi = {
  get: () => api.get<{ scope: any; exists: boolean }>('/isms-scope'),
  save: (data: any) => api.put<{ scope: any; saved: boolean }>('/isms-scope', data),
  publish: () => api.post<{ previousStatus: string; newStatus: string }>('/isms-scope/publish', {}),
};

// Management Reviews API (ISO 27001 Clause 9.3)
export type ManagementReviewStatus = 'draft' | 'submitted' | 'approved';

export interface ManagementReviewSummary {
  reviewId: string;
  title: string;
  period: string;
  meetingDate: string;
  reviewStartDate?: string;
  status: ManagementReviewStatus;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  approvedAt?: string;
}

export interface ManagementReviewSection {
  aiDraft: string | null;
  humanText: string;
  aiGeneratedAt: string | null;
}

export interface ManagementReview extends ManagementReviewSummary {
  submittedAt?: string;
  submittedBy?: string;
  snapshot: Record<string, any>;
  inputs: Record<string, ManagementReviewSection>;
  outputs: {
    decisions: any[];
    ismsChanges: any[];
    resourceNeeds: string;
    riskActions: any[];
  };
  previousReviewId?: string;
  previousReviewOutputs?: Record<string, any>;
  previousReviewTitle?: string;
}

export const managementReviewApi = {
  list: async (): Promise<ManagementReviewSummary[]> => {
    const resp = await api.get<{ reviews: ManagementReviewSummary[] }>('/management-reviews');
    return resp.reviews || [];
  },

  create: (data: { title: string; period: string; meetingDate?: string; reviewStartDate?: string }) =>
    api.post<ManagementReview>('/management-reviews', data),

  get: (reviewId: string) =>
    api.get<ManagementReview>(`/management-reviews/${reviewId}`),

  update: (reviewId: string, data: Partial<ManagementReview>) =>
    api.put<ManagementReview>(`/management-reviews/${reviewId}`, data),

  delete: (reviewId: string) =>
    api.delete<any>(`/management-reviews/${reviewId}`),

  submit: (reviewId: string) =>
    api.post<ManagementReview>(`/management-reviews/${reviewId}/submit`, {}),

  approve: (reviewId: string) =>
    api.post<ManagementReview>(`/management-reviews/${reviewId}/approve`, {}),

  returnToDraft: (reviewId: string) =>
    api.post<ManagementReview>(`/management-reviews/${reviewId}/return-to-draft`, {}),
};

// PESTLE Assessment API
export const pestleApi = {
  list: () => api.get<{ assessments: any[] }>('/pestle'),
  create: (data: { title: string; description?: string }) =>
    api.post<{ assessment: any }>('/pestle', data),
  get: (pestleId: string) =>
    api.get<{ assessment: any }>(`/pestle/${pestleId}`),
  update: (pestleId: string, data: { title?: string; description?: string; categoryAnalyses?: Record<string, string>; status?: string; factors?: any[] }) =>
    api.put<{ assessment: any }>(`/pestle/${pestleId}`, data),
  delete: (pestleId: string) =>
    api.delete<any>(`/pestle/${pestleId}`),
  getSettings: () =>
    api.get<{ settings: { nextReviewDate?: string } }>('/pestle/settings'),
  updateSettings: (data: { nextReviewDate?: string }) =>
    api.put<{ settings: any }>('/pestle/settings', data),
};

// AI Use Case Register API (AI Governance)
const AI_USE_CASES_BASE = '/ai-use-cases';

type AiUseCaseListEnvelope = { useCases?: AiUseCase[]; items?: AiUseCase[] };
type AiUseCaseEnvelope = { useCase?: AiUseCase } & Partial<AiUseCase>;

const unwrapUseCases = (resp: AiUseCaseListEnvelope): AiUseCase[] =>
  resp.useCases ?? resp.items ?? [];

const unwrapUseCase = (resp: AiUseCaseEnvelope): AiUseCase =>
  (resp.useCase ?? resp) as AiUseCase;

export const aiUseCaseApi = {
  list: async (): Promise<AiUseCase[]> => {
    const resp = await api.get<AiUseCaseListEnvelope>(AI_USE_CASES_BASE);
    return unwrapUseCases(resp);
  },

  get: async (useCaseId: string): Promise<AiUseCase> => {
    const resp = await api.get<AiUseCaseEnvelope>(`${AI_USE_CASES_BASE}/${useCaseId}`);
    return unwrapUseCase(resp);
  },

  create: async (payload: Partial<AiUseCase>): Promise<AiUseCase> => {
    const resp = await api.post<AiUseCaseEnvelope>(AI_USE_CASES_BASE, payload);
    return unwrapUseCase(resp);
  },

  update: async (useCaseId: string, payload: Partial<AiUseCase>): Promise<AiUseCase> => {
    const resp = await api.put<AiUseCaseEnvelope>(`${AI_USE_CASES_BASE}/${useCaseId}`, payload);
    return unwrapUseCase(resp);
  },

  /**
   * Move a use case through the lifecycle. `text` is the approver's own words — the
   * conditions attached to an approval, or the reason for a rejection — and is the only part
   * of the decision the client supplies: identity and timestamp are stamped server-side from
   * the verified caller, and are ignored if sent.
   *
   * Sent under the key the target status expects, because the Lambda requires a `reason` on a
   * rejection (400 without one) and reads `conditions` on an approval. Both keys are accepted
   * either way, but sending the right one keeps the request legible in a log.
   */
  transition: async (
    useCaseId: string,
    status: AiUseCaseStatus,
    text?: string,
    reviewOverrideReason?: string,
  ): Promise<AiUseCase> => {
    const trimmed = (text || '').trim();
    const body: { status: AiUseCaseStatus; reason?: string; conditions?: string; reviewOverrideReason?: string } = { status };
    if (trimmed) {
      if (status === 'rejected') body.reason = trimmed;
      else body.conditions = trimmed;
    }
    const trimmedOverride = (reviewOverrideReason || '').trim();
    if (trimmedOverride) body.reviewOverrideReason = trimmedOverride;
    const resp = await api.post<AiUseCaseEnvelope>(
      `${AI_USE_CASES_BASE}/${useCaseId}/transition`,
      body
    );
    return unwrapUseCase(resp);
  },

  /** Re-runs approval-time drafting for one already-approved use case - safe to call
   *  repeatedly, the server only ever does real work for whichever step never completed. */
  retryDrafting: async (useCaseId: string): Promise<AiUseCase> => {
    const resp = await api.post<AiUseCaseEnvelope>(`${AI_USE_CASES_BASE}/${useCaseId}/retry-drafting`, {});
    return unwrapUseCase(resp);
  },

  /** Compliance checklist item 5 (Pending AI Use Cases) - the one manual step. The server
   *  re-derives item 4's own "done" state itself and 400s if it isn't, rather than trusting
   *  the client's belief that it's ready. */
  markPrivacyPolicySent: async (useCaseId: string): Promise<AiUseCase> => {
    const resp = await api.post<AiUseCaseEnvelope>(`${AI_USE_CASES_BASE}/${useCaseId}/mark-privacy-policy-sent`, {});
    return unwrapUseCase(resp);
  },

  getAudit: (useCaseId: string) =>
    api.get<{ events: AiUseCaseAuditEvent[] }>(`${AI_USE_CASES_BASE}/${useCaseId}/audit`),

  delete: (useCaseId: string) =>
    api.delete<{ deleted?: boolean }>(`${AI_USE_CASES_BASE}/${useCaseId}`),
};

// Bedrock review pass for one AI use case - async, poll GET until status leaves 'generating'.
export const aiUseCaseReviewApi = {
  start: (useCaseId: string) =>
    api.post<{ reviewId: string; status: string }>(`${AI_USE_CASES_BASE}/${useCaseId}/review/start`, {}),
  get: (useCaseId: string) =>
    api.get<{ review: any }>(`${AI_USE_CASES_BASE}/${useCaseId}/review`),
};

// Generic, reusable "who's reviewing this" module (entityType/entityId keyed - not owned by
// or specific to AI use cases, even though that's its first real consumer).
export const reviewAssignmentsApi = {
  listForEntity: (entityType: string, entityId: string) =>
    api.get<{ assignments: any[]; count: number }>(
      `/review-assignments?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`,
    ),
  listForAssignee: (assignedToUserId: string) =>
    api.get<{ assignments: any[]; count: number }>(
      `/review-assignments?assignedToUserId=${encodeURIComponent(assignedToUserId)}`,
    ),
  create: (data: any) => api.post<{ assignment: any }>('/review-assignments', data),
  update: (assignmentId: string, data: any) => api.put<{ assignment: any }>(`/review-assignments/${assignmentId}`, data),
  delete: (assignmentId: string) => api.delete<{ success: boolean }>(`/review-assignments/${assignmentId}`),
};

// Meeting Minutes Register API
const MEETING_MINUTES_BASE = '/meeting-minutes';

// The Lambda returns `{ minutesList: [...] }` / `{ minutes: {...} }`; the
// alternatives below are tolerated so a shape change doesn't blank the UI.
type MeetingMinutesListEnvelope =
  | MeetingMinutes[]
  | {
      minutesList?: MeetingMinutes[];
      minutes?: MeetingMinutes[];
      items?: MeetingMinutes[];
    };

type MeetingMinutesEnvelope = { minutes?: MeetingMinutes } & Partial<MeetingMinutes>;

const unwrapMinutesList = (resp: MeetingMinutesListEnvelope): MeetingMinutes[] => {
  if (Array.isArray(resp)) return resp;
  if (!resp) return [];
  return (
    resp.minutesList ??
    (Array.isArray(resp.minutes) ? resp.minutes : undefined) ??
    resp.items ??
    []
  );
};

const unwrapMinutes = (resp: MeetingMinutesEnvelope): MeetingMinutes =>
  (resp?.minutes ?? resp) as MeetingMinutes;

export const meetingMinutesApi = {
  /** Pass an initiativeId to list only the minutes pinned to that initiative. */
  list: async (initiativeId?: string): Promise<MeetingMinutes[]> => {
    const qs = initiativeId ? `?initiativeId=${encodeURIComponent(initiativeId)}` : '';
    const resp = await api.get<MeetingMinutesListEnvelope>(`${MEETING_MINUTES_BASE}${qs}`);
    return unwrapMinutesList(resp);
  },

  get: async (minutesId: string): Promise<MeetingMinutes> => {
    const resp = await api.get<MeetingMinutesEnvelope>(`${MEETING_MINUTES_BASE}/${minutesId}`);
    return unwrapMinutes(resp);
  },

  create: async (payload: Partial<MeetingMinutes>): Promise<MeetingMinutes> => {
    const resp = await api.post<MeetingMinutesEnvelope>(MEETING_MINUTES_BASE, payload);
    return unwrapMinutes(resp);
  },

  update: async (minutesId: string, payload: Partial<MeetingMinutes>): Promise<MeetingMinutes> => {
    const resp = await api.put<MeetingMinutesEnvelope>(
      `${MEETING_MINUTES_BASE}/${minutesId}`,
      payload
    );
    return unwrapMinutes(resp);
  },

  /** `status` is not writable via update — it moves only through this endpoint. */
  transition: async (minutesId: string, status: MinutesStatus): Promise<MeetingMinutes> => {
    const resp = await api.post<MeetingMinutesEnvelope>(
      `${MEETING_MINUTES_BASE}/${minutesId}/transition`,
      { status }
    );
    return unwrapMinutes(resp);
  },

  /** Closes one action entry (open -> done) on its OWN minutes record — the entryId must
   *  belong to `minutesId`. Unlike `update`, this works even when that record is approved:
   *  an open action doesn't stop needing to be closed just because its meeting got signed
   *  off, so this narrow endpoint is exempt from the approval lock. Always call this to
   *  close an action rather than PUTting an edited `entries` array, including when closing
   *  an action from somewhere other than that meeting's own page (e.g. from another
   *  meeting's carry-forward panel, or from an initiative's Actions tab). */
  closeAction: async (minutesId: string, entryId: string): Promise<MeetingMinutes> => {
    const resp = await api.post<MeetingMinutesEnvelope>(
      `${MEETING_MINUTES_BASE}/${minutesId}/close-action`,
      { entryId }
    );
    return unwrapMinutes(resp);
  },

  delete: (minutesId: string) =>
    api.delete<{ deleted?: boolean }>(`${MEETING_MINUTES_BASE}/${minutesId}`),
};

// Record of Processing Activities API (GDPR Article 30)
const PROCESSING_ACTIVITIES_BASE = '/processing-activities';

// The Lambda returns `{ activities: [...] }` / `{ activity: {...} }`; the alternatives
// below are tolerated so a shape change doesn't blank the UI.
type ActivityListEnvelope =
  | ProcessingActivity[]
  | { activities?: ProcessingActivity[]; items?: ProcessingActivity[] };

type ActivityEnvelope = { activity?: ProcessingActivity } & Partial<ProcessingActivity>;

const unwrapActivityList = (resp: ActivityListEnvelope): ProcessingActivity[] => {
  if (Array.isArray(resp)) return resp;
  if (!resp) return [];
  return resp.activities ?? resp.items ?? [];
};

const unwrapActivity = (resp: ActivityEnvelope): ProcessingActivity =>
  (resp?.activity ?? resp) as ProcessingActivity;

export const processingActivityApi = {
  list: async (): Promise<ProcessingActivity[]> => {
    const resp = await api.get<ActivityListEnvelope>(PROCESSING_ACTIVITIES_BASE);
    return unwrapActivityList(resp);
  },

  get: async (activityId: string): Promise<ProcessingActivity> => {
    const resp = await api.get<ActivityEnvelope>(`${PROCESSING_ACTIVITIES_BASE}/${activityId}`);
    return unwrapActivity(resp);
  },

  create: async (payload: Partial<ProcessingActivity>): Promise<ProcessingActivity> => {
    const resp = await api.post<ActivityEnvelope>(PROCESSING_ACTIVITIES_BASE, payload);
    return unwrapActivity(resp);
  },

  update: async (
    activityId: string,
    payload: Partial<ProcessingActivity>,
  ): Promise<ProcessingActivity> => {
    const resp = await api.put<ActivityEnvelope>(
      `${PROCESSING_ACTIVITIES_BASE}/${activityId}`,
      payload,
    );
    return unwrapActivity(resp);
  },

  /**
   * `status` is not writable via update — it moves only through this endpoint, and the
   * server re-validates Article 30's mandatory content on the move to `active`.
   */
  transition: async (activityId: string, status: ActivityStatus): Promise<ProcessingActivity> => {
    const resp = await api.post<ActivityEnvelope>(
      `${PROCESSING_ACTIVITIES_BASE}/${activityId}/transition`,
      { status },
    );
    return unwrapActivity(resp);
  },

  delete: (activityId: string) =>
    api.delete<{ deleted?: boolean }>(`${PROCESSING_ACTIVITIES_BASE}/${activityId}`),

  /**
   * Manual, on-demand DPIA screening — a lightweight, field-derived heuristic (special
   * category data, a vulnerable group, large-scale volume, or a third-country transfer),
   * not the full EDPB engine. Writes dpiaVerdict/lastScreenedAt, and creates a linked draft
   * DPIA the first time the verdict comes back 'required'.
   */
  screenForDpia: async (activityId: string): Promise<ProcessingActivity> => {
    const resp = await api.post<ActivityEnvelope>(
      `${PROCESSING_ACTIVITIES_BASE}/${activityId}/screen-dpia`,
      {},
    );
    return unwrapActivity(resp);
  },
};
